import Box from '../../../../common/components/box/Box';
import Logger from '../../../../common/services/Logger';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import styles from './AlarmTab.module.scss';
import Loading from '../../../../common/services/Loading';
import AlarmsService from '../../../../api/alarms/AlarmsService';
import { DEFAULT_LANGUAGE, LOGGER_LOG_TYPE, STORAGE } from '../../../../Config';
import ToggleSwitch from 'common/components/toggleSwitch/ToggleSwitch';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import { Col, Row } from 'react-flexbox-grid';
import InputController from 'common/components/input/InputController';
import InputError from 'common/components/inputError/InputError';
import { AlarmSettingDaysOfWeekDto, AlarmSettingDto, AlarmTypeDto, AlarmTypeFieldDto } from 'api/alarms/models/AlarmSettingDto';
import DrawTools, { LocationArea } from 'common/components/maps/DrawTools';
import { Circle, MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';
import { useHistory, useParams } from 'react-router-dom';
import TimePickerController from 'common/components/timePicker/TimePickerController';
import SelectController from 'common/components/select/SelectController';
import Utils from 'common/services/Utils';
import InputGroupController from 'common/components/inputGroup/InputGroupController';
import ZoomLevelControl from 'common/components/maps/ZoomLevelControl';
import moment from 'moment';
import Storage from '../../../../common/services/Storage';
import toggleSwitchStyle from 'common/components/toggleSwitch/ToggleSwitch.module.scss'
import AsyncSelect from 'react-select/async';
import colors from 'styles/export/colors.module.scss';
import GeofencesService from 'api/geofences/GeofencesService';
import { addressFormat } from 'common/services/GeoLocator';
import { AddressOption, markerIcon } from 'screens/geofence/GeofenceScreen';
import { LatLngExpression } from 'leaflet';
import { OptionTypeBase } from 'react-select';
import CheckBoxController from 'common/components/checkBox/CheckBoxController';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import useAlarmRecipients, { alarmTypeToCommunicationName } from 'screens/alarms/hooks/useAlarmRecipients';
import { FaInfoCircle, FaCheck, FaTimes } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';

type Props = {
    alarm: AlarmSettingDto | null;
    onSelectDayOfWeek: (day: number, value: boolean) => void;
    daysOfWeekSelected: AlarmSettingDaysOfWeekDto[];
    form: any;
} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;


const AlarmTab = React.forwardRef<HTMLDivElement, Props>(({ alarm, form, daysOfWeekSelected, onSelectDayOfWeek }, ref) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasSmsPackage = userProfile?.hasSmsPackage === true;

    const [selectedAlarmType, setSelectedAlarmType] = useState<AlarmTypeDto>();
    const { recipients, loading: recipientsLoading } = useAlarmRecipients(selectedAlarmType?.name);

    const { type } = useParams<{ type: string }>();
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');

    const [alarmTypes, setAlarmTypes] = useState<AlarmTypeDto[]>([]);

    const [area, setArea] = useState<LocationArea>();
    const [position, setPosition] = useState<GeolocationPosition>();
    const [zoomDefault, setZoomDefault] = useState<number>(12);

    const { handleSubmit, setValue, errors, register } = form;

    const [refresh, setRefresh] = useState<number>(1);

    const [formValues, setFormValues] = useState<any>({});
    const [reloadData, setReloadData] = useState<boolean>(false);
    const [loadEditData, setLoadEditData] = useState<boolean>(false);

    const [defaultWeekdays, setDefaultWeekdays] = useState<string[]>([]);

    const timerId = useRef<NodeJS.Timeout | null>(null);

    const [markerPosition, setMarkerPosition] = useState<LatLngExpression | null>(null);
    const [selectedAddress, setSelectedAddress] = useState<OptionTypeBase | null>(null);

    const [typesLoaded, setTypesLoaded] = useState(false);

    const weekdaysChecked = (i: number) => {
        return daysOfWeekSelected.map(x => x.dayOfWeek).includes(i);
    };

    const getData = async () => {
        try {
            Loading.show();

            const types = await AlarmsService.getTypes();
            setAlarmTypes(types);
            setTypesLoaded(true);

            setReloadData(true);

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get information to create alarm`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            Loading.hide();
        }
    }

    const loadData = async () => {
        const currentValues = form.getValues();
        const isFormEmpty = Object.values(currentValues).every(v => v === undefined || v === null || v === '');

        if (!alarm && isFormEmpty) { return; } // Verifica se há valores armazenados localmente

        try {
            if (type == 'edit' && isFormEmpty && !loadEditData) {
                setFormValues(alarm);
                setLoadEditData(true);
            }
            const alarmData = isDetails ? alarm : formValues;

            if (alarmData.type) {
                const typeSelected = alarmTypes.find(x => x.name === alarmData.type);
                onChangeType(typeSelected);
            }

            const dataMappings = {
                id: alarm?.id,
                rowVersion: alarm?.rowVersion,
                name: alarmData.name,
                enabled: alarmData.enabled,
                notificationsEnabled: alarmData.notificationsEnabled,
                alertEnabled: alarmData.alertEnabled ?? true,
                emailEnabled: alarmData.emailEnabled ?? true,
                smsEnabled: alarmData.smsEnabled ?? true,
                startHour: alarmData.startHour,
                stopHour: alarmData.stopHour,
                limit: alarmData.limit,
                latitude: alarmData.latitude,
                longitude: alarmData.longitude,
                radius: alarmData.radius,
                duration: alarmData.duration,
                address: undefined
            };

            Object.entries(dataMappings).forEach(([key, value]) => setValue(key, value));

            if (alarmData.latitude && alarmData.longitude && alarmData.radius) {
                const areaAux = { latitude: alarmData.latitude, longitude: alarmData.longitude, mRadius: alarmData.radius, radius: alarmData.radius };
                setMarkerPosition({ lat: alarmData.latitude, lng: alarmData.longitude });
                onCreateArea(areaAux);
            }
            setZoomDefault(alarmData.zoom ?? zoomDefault);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't load alarm information`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const getFieldsToReset = (previousType?: AlarmTypeDto, newType?: AlarmTypeDto): string[] => {
        if (!previousType) return [];

        const previousFields = previousType.fields.map(f => f.name);
        const newFields = newType?.fields.map(f => f.name) ?? [];

        return previousFields.filter(field => !newFields.includes(field));
    };

    const clearFieldsSelectively = (fieldsToClear: string[]) => {
        const fieldResetMap: Record<string, () => void> = {
            'name': () => setValue('name', ''),
            'start_hour': () => setValue('startHour', undefined),
            'stop_hour': () => setValue('stopHour', undefined),
            'limit': () => setValue('limit', undefined),
            'latitude': () => setValue('latitude', undefined),
            'longitude': () => setValue('longitude', undefined),
            'radius': () => setValue('radius', undefined),
            'duration': () => setValue('duration', undefined),
        };

        fieldsToClear.forEach(field => {
            if (fieldResetMap[field]) {
                fieldResetMap[field]();
            }
        });

        if (fieldsToClear.some(f => ['latitude', 'longitude', 'radius'].includes(f))) {
            setArea(undefined);
            setMarkerPosition(null);
            setSelectedAddress(null);
        }
    };


    const onChangeType = (at?: AlarmTypeDto) => {
        const fieldsToReset = getFieldsToReset(selectedAlarmType, at);
        clearFieldsSelectively(fieldsToReset);
        setSelectedAlarmType(at);
        form.setValue('type', at ? at.name : undefined);
    }


    // const clearForm = () => {
    //     setValue('rowVersion', alarm?.rowVersion);
    //     setValue('type', undefined);
    //     setValue('name', '');
    //     setValue('enabled', false);
    //     setValue('notificationsEnabled', false);
    //     setValue('alertEnabled', true);
    //     setValue('emailEnabled', true);
    //     setValue('smsEnabled', hasSmsPackage);
    //     setValue('startHour', undefined);
    //     setValue('stopHour', undefined);
    //     setValue('limit', undefined);
    //     setValue('latitude', undefined);
    //     setValue('longitude', undefined);
    //     setValue('radius', undefined);
    // };

    const onCreateArea = (a: LocationArea) => {
        setArea(a);
        setValue('latitude', a.latitude);
        setValue('longitude', a.longitude);
        setValue('radius', a.mRadius);
    }

    const setCurrentPosition = (p: GeolocationPosition) => {
        setPosition(p);
        setRefresh(refresh + 1);
    }

    const onChangeCoordinates = () => {
        if (form.getValues('latitude') && form.getValues('longitude')) {
            const p: GeolocationPosition = {
                coords: {
                    latitude: form.getValues('latitude'),
                    longitude: form.getValues('longitude'),
                    accuracy: 0,
                    altitude: null,
                    altitudeAccuracy: null,
                    heading: null,
                    speed: null
                },
                timestamp: Date.now()
            };
            setCurrentPosition(p);
            setZoomDefault(zoomDefault);
            const radius = form.getValues('radius');
            if (radius && Number(radius) > 0) {
                setArea({ latitude: form.getValues('latitude'), longitude: form.getValues('longitude'), mRadius: radius, radius: radius });
            } else {
                setArea(undefined);
            }
        }
    }

    function LocationMarker() {
        const map = useMapEvents({
            async click(e) {
                if (isDetails) return;

                try {
                    const res: any = await GeofencesService.searchByLatLong(e.latlng.lat, e.latlng.lng)
                    setValue('address', addressFormat(res, res.lat!, res.lon!))

                } catch (e) {
                    addToast(t('common.messages.error_load_info'), { appearance: 'error' });
                } finally {
                    setCurrentArea(e.latlng.lat, e.latlng.lng)
                }
            },
        })

        const handleDragEnd = async (event: any) => {
            const latlng = event.target.getLatLng();
            setCurrentArea(latlng.lat, latlng.lng);
            try {
                const res: any = await GeofencesService.searchByLatLong(latlng.lat, latlng.lng)
                setValue('address', addressFormat(res, res.lat!, res.lon!))
            } catch (err) {
                addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            }
        }

        return markerPosition === null ? null : (
            <Marker
                position={markerPosition}
                icon={markerIcon}
                draggable={!isDetails}
                eventHandlers={{
                    dragend: handleDragEnd
                }}
            />
        )
    }

    const renderMap = () => {
        return <React.Fragment key={`map_container_${refresh}`}>
            <MapContainer
                key={`map_${refresh}`}
                center={[area ? area.latitude : position?.coords?.latitude ?? 52.516316, area ? area.longitude : position?.coords?.longitude ?? 13.377825]}
                maxZoom={18} minZoom={6} zoom={zoomDefault} scrollWheelZoom={true} zoomSnap={0.1} zoomDelta={0.5} className={styles.map}>
                {zoomDefault && <ZoomLevelControl onZoomChange={zoom => { setZoomDefault(zoom); setValue('zoom', zoom); }} />}
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {area ? <Circle center={[area.latitude, area.longitude]} radius={area.mRadius}></Circle> :
                    (isDetails ? null : <DrawTools onCreated={onCreateArea} />)
                }
                <LocationMarker />
            </MapContainer>
        </React.Fragment>
    }

    const renderForm = (f: AlarmTypeFieldDto) => {
        switch (f.name) {
            case 'name': return (
                <Col xs={12} key={`${f.name}_${refresh}`}>
                    <FormItem key={`${f.name}_${refresh}`}>
                        <Label className={styles.label}>{t('alarms.list.name')}{!isDetails && f.required ? '*' : ''}</Label>
                        <InputController
                            name="name"
                            form={form as any}
                            autoComplete='off'
                            disabled={isDetails}
                            placeholder={t('alarms.list.name')}
                            rules={{ required: !isDetails && f.required, validate: value => !Utils.isStringNullOrEmpty(value), maxLength: 250 }}
                        />
                        <InputError error={errors.name} maxLength={250} />
                    </FormItem>

                </Col>

            )
            case 'start_hour': return (
                <React.Fragment key={`${f.name}_${refresh}_fragment`}>
                    <Col xs={12}>
                        <Label className={styles.label}>{t('alarms.day_of_week')}{!isDetails && f.required ? '*' : ''}</Label>
                    </Col>
                    { defaultWeekdays.map((day, index) => (
                        <Col xs={12} md={3} lg={1} key={`${refresh}-day--${day}`} className="mb-3">
                            <FormItem key={`${f.name}_${refresh}-day--${day}`}>
                                <Label>{day}</Label>
                                <label
                                    style={{ cursor: isDetails ? 'no-drop' : 'pointer' }}
                                    className={`${toggleSwitchStyle.toggleSwitch} ${toggleSwitchStyle.enabled}`}>
                                    <input
                                        type='checkbox'
                                        id={`checkbox_day--${day}`}
                                        checked={weekdaysChecked(index)}
                                        onChange={e => onSelectDayOfWeek(index, e.target.checked)}
                                        disabled={isDetails}
                                    />
                                    <span className={toggleSwitchStyle.switch} />
                                </label>
                            </FormItem>
                        </Col>))}
                    <Col xs={12} md={6} key={`${f.name}_${refresh}`}>
                        <FormItem key={`${f.name}_${refresh}`}>
                            <Label className={styles.label}>{t('alarms.start_hour')}{!isDetails && f.required ? '*' : ''}</Label>
                            <TimePickerController
                                form={form}
                                name="startHour"
                                isDisabled={isDetails}
                                timePlaceholder={t('alarms.start_hour')}
                                isClearable={true}
                            />

                            <InputError error={errors.startHour} />
                        </FormItem>
                    </Col></React.Fragment>
            )
            case 'stop_hour': return (
                <Col xs={12} md={6} key={`${f.name}_${refresh}`}>
                    <FormItem key={`${f.name}_${refresh}`}>
                        <Label className={styles.label}>{t('alarms.stop_hour')}{!isDetails && f.required ? '*' : ''}</Label>

                        <TimePickerController
                            form={form}
                            name="stopHour"
                            isDisabled={isDetails}
                            timePlaceholder={t('alarms.stop_hour')}
                            isClearable={true}
                        />
                        <InputError error={errors.stopHour} />

                    </FormItem>
                </Col>
            )
            case 'limit': return (
                <Col xs={12} md={12} key={`${f.name}_${refresh}`}>
                    <FormItem key={`${f.name}_${refresh}`}>
                        <Label className={styles.label}>{t('alarms.limit')}{!isDetails && f.required ? '*' : ''}</Label>
                        <InputController
                            name={f.name}
                            form={form as any}
                            type="number"
                            min="0"
                            autoComplete='off'
                            disabled={isDetails}
                            placeholder={t('alarms.limit')}
                            rules={{ required: !isDetails && f.required }}
                        />
                        <InputError error={errors.limit} />
                    </FormItem>
                </Col>
            )
            case 'latitude': case 'longitude': return (
                <Col xs={12} md={6} xl={3} key={`${f.name}_${refresh}`}>
                    <Label className={styles.label}>{t('alarms.' + f.name as any)}{!isDetails && f.required ? '*' : ''}</Label>
                    <InputController
                        key={f.name}
                        name={f.name}
                        type="number"
                        min="0"
                        form={form as any}
                        autoComplete='off'
                        disabled={isDetails}
                        placeholder={t('alarms.latitude')}
                        rules={{ required: !isDetails && f.required }}
                        onKeyPress={(e: any) => {
                            if (e.key === 'Enter') {
                                onChangeCoordinates();
                            }
                        }}
                        onBlur={() => {
                            onChangeCoordinates();
                        }}
                    />
                    <InputError error={errors[f.name]} />
                </Col>
            )
            case 'duration': return (
                <Col xs={12} md={isDetails ? 6 : 4} xl={isDetails ? 3 : 2} key={`${f.name}_${refresh}`}>
                    <FormItem key={`${f.name}_${refresh}`}>
                        <Label className={styles.label}>{t('alarms.duration')}{!isDetails && f.required ? '*' : ''}</Label>
                        <InputController
                            name={f.name}
                            form={form as any}
                            type="time"
                            disabled={isDetails}
                            placeholder={t('alarms.duration')}
                            rules={{ required: !isDetails && f.required }}
                        />
                        <InputError error={errors.limit} />
                    </FormItem>
                </Col>
            );
            case 'radius': return (<React.Fragment key={`${f.name}_${refresh}_fragment`}>
                <Col xs={12} md={6} xl={3} key={`${f.name}_${refresh}`}>
                    <Label className={styles.label}>{t('alarms.radius')}{!isDetails && f.required ? '*' : ''}</Label>
                    <InputGroupController
                        name={f.name}
                        text={t('common.meters')}
                        type="number"
                        min="0"
                        form={form as any}
                        autoComplete='off'
                        disabled={isDetails}
                        placeholder={t('alarms.radius')}
                        rules={{ required: !isDetails && f.required }}
                        onKeyPress={(e: any) => {
                            if (e.key === 'Enter') {
                                onChangeCoordinates();
                            }
                        }}
                        onBlur={() => {
                            onChangeCoordinates();
                        }}
                    />

                    <InputError error={errors[f.name]} />
                </Col>

                {!isDetails &&
                    <React.Fragment key={`${f.name}_${refresh}_address`}>
                        <Col xs={12}>
                            <div className={styles.hrLine}></div>
                        </Col>

                        <Col xs={12}>
                            <Label className={styles.label}>
                                {t('geofences.search_by_address')}
                            </Label>

                            <AsyncSelect loadOptions={promiseOptions}
                                value={selectedAddress}
                                onChange={data => {
                                    setSelectedAddress(data);
                                    if (data) {
                                        setCurrentArea(data.lat, data.long);
                                    }
                                }}
                                noOptionsMessage={() => t('common.no_options')}
                                loadingMessage={() => t('common.searching')}
                                placeholder={t('geofences.search_by_address')}
                                styles={{
                                    control: (base, state) => ({
                                        ...base,
                                        backgroundColor: colors.white,
                                        borderRadius: '20px !important',
                                        border: state.isFocused ? `1px solid ${colors.success}` : `1px solid ${colors.mercury}`,
                                        boxShadow: 'none',
                                        ':hover': {
                                            borderColor: state.isFocused ? colors.success : colors.mercury,
                                        },
                                        zIndex: 0
                                    }),
                                    menuPortal: base => ({
                                        ...base,
                                        zIndex: 9999
                                    }),
                                    placeholder: base => ({
                                        ...base,
                                        color: colors.frenchGray,
                                        opacity: 0.54,
                                    }),
                                    valueContainer: base => ({
                                        ...base,
                                        padding: '5.75px 1rem',
                                        justifyContent: 'initial',
                                        zIndex: 0
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        color: state.isSelected ? colors.white : colors.black,
                                        backgroundColor: state.isSelected ? colors.primaryDark : colors.white,
                                        ':hover': {
                                            backgroundColor: colors.lightGrayishBlue,
                                            color: colors.black
                                        },
                                    }),
                                    singleValue: base => ({
                                        ...base,
                                        color: colors.lightblack,
                                    }),
                                    multiValueLabel: base => ({
                                        ...base,
                                        backgroundColor: colors.solitude,
                                        color: colors.black,
                                    }),
                                    multiValueRemove: base => ({
                                        ...base,
                                        backgroundColor: colors.solitude,
                                        color: colors.black,
                                    }),
                                    indicatorSeparator: base => ({
                                        ...base,
                                        width: '1px',
                                        backgroundColor: colors.mercury
                                    }),
                                }}
                            />
                        </Col>

                    </React.Fragment>}
                <Col xs={12}>
                    <Row className={styles.map} key={`map_${ref}`} >
                        {renderMap()}
                    </Row>
                </Col>
            </React.Fragment>)
            default:
                break;
        }
    }

    const promiseOptions = (inputValue: string, callback: (options: AddressOption[]) => void) => {
        if (timerId.current) clearTimeout(timerId.current);

        timerId.current = setTimeout(async () => {
            const result = await onSearchAddress(inputValue);
            callback(result);
        }, 500);
    }

    const setCurrentArea = (lat: number, lng: number) => {
        setMarkerPosition([lat, lng])

        const radius = form.getValues('radius');
        if (radius && Number(radius) > 0) {
            setArea({ latitude: lat, longitude: lng, mRadius: radius, radius: radius });
        } else {
            setArea(undefined);
        }

        const p: GeolocationPosition = {
            coords: {
                latitude: lat,
                longitude: lng,
                accuracy: 0,
                altitude: null,
                altitudeAccuracy: null,
                heading: null,
                speed: null
            },
            timestamp: Date.now()
        };
        setCurrentPosition(p);
        setZoomDefault(zoomDefault);

        form.setValue('latitude', lat);
        form.setValue('longitude', lng);
    }


    const onSearchAddress = async (value: string) => {
        try {
            const res: any = await GeofencesService.searchByAddress(value ?? '');
            if (!res || res.length == 0) return []

            return res.map((r: any) => (
                { label: addressFormat(r, r.lat, r.lon), lat: r.lat, long: r.lon, value: `${r.lat},${r.lon}`, address: addressFormat(r, r.lat, r.lon) }
            ))
        } catch (e) {
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }

    }

    useEffect(() => {
        getData();
        navigator.geolocation.getCurrentPosition((x: GeolocationPosition) => setCurrentPosition(x));
    }, []);


    useEffect(() => {
        setIsDetails(type === 'details');
        setDefaultWeekdays(Array(... new Array(7)).map(function (_, i) {
            moment.locale(Storage.get(STORAGE.CURRENT_LOCALE) ?? DEFAULT_LANGUAGE, { week: { dow: 0 } });
            return moment().startOf('week').weekday(i).format('ddd');
        }));

        if (type !== 'details') {
            setFormValues(form.getValues());
        }
    }, [type]);


    useEffect(() => {
        if (!typesLoaded) return;
        loadData();
    }, [alarm, reloadData, loadEditData, typesLoaded]);

    // Quando ativa notificações, auto-ativar canais
    const notificationsEnabled = form.watch('notificationsEnabled');
    const alertEnabled = form.watch('alertEnabled');
    const emailEnabled = form.watch('emailEnabled');
    const smsEnabled = form.watch('smsEnabled');
    useEffect(() => {
        if (notificationsEnabled && !isDetails) {
            setValue('alertEnabled', true);
            setValue('emailEnabled', true);
            setValue('smsEnabled', hasSmsPackage);
        }
    }, [notificationsEnabled]);

    return (
        <form onSubmit={handleSubmit} key={'form_' + refresh}>
            <Box className={styles.box}>
                {alarmTypes && alarmTypes.length > 0 && <Row>
                    <Col xl={12} key={'type' + ref}>
                        <FormItem>
                            <Label className={styles.label}>{t('alarms.list.type')}*</Label>

                            <SelectController
                                ref={register({ required: true })}
                                form={form}
                                name="type"
                                isClearable={true}
                                placeholder={t('common.select_option')}
                                options={alarmTypes?.map(x => ({
                                    value: x.name,
                                    label: t('alarms.types.' + x.name as any)
                                })).sort((a, b) => a.label.localeCompare(b.label))}
                                isDisabled={isDetails}
                                rules={{ required: !isDetails }}
                                onChange={(x: any) => {
                                    const selected = x ? alarmTypes.find(y => y.name === x.value) : undefined;
                                    onChangeType(selected);
                                }}

                            />
                            {!isDetails && <InputError error={errors.type} />}
                        </FormItem>
                    </Col>
                </Row>}

                <Row>
                    {selectedAlarmType?.fields.map(renderForm)}
                </Row>

                <Row>
                    <Col xl={12}>
                        <FormItem key={`enabled_${refresh}`}>
                            <Label className={styles.label}>{t('alarms.enabled')}</Label>
                            <ToggleSwitch
                                name="enabled"
                                form={form}
                                isDetails={isDetails}
                            />
                        </FormItem>
                    </Col>

                    <Col xl={12}>
                        <FormItem key={`sendNotification_${refresh}`}>
                            <Label className={styles.label}>{t('alarms.send_notification')}</Label>
                            <ToggleSwitch
                                name="notificationsEnabled"
                                form={form}
                                isDetails={isDetails}
                            />
                        </FormItem>
                    </Col>

                    {form.watch('notificationsEnabled') && (
                        <Col xl={12}>
                            <FormItem key={`notificationChannels_${refresh}`}>
                                <Label className={styles.label}>{t('alarms.notification_channels')}</Label>
                                <div className={styles.notificationChannels}>
                                    <CheckBoxController
                                        form={form}
                                        name="alertEnabled"
                                        label={t('alarms.channel_alert')}
                                        disabled={isDetails}
                                    />
                                    <CheckBoxController
                                        form={form}
                                        name="emailEnabled"
                                        label={t('alarms.channel_email')}
                                        disabled={isDetails}
                                    />
                                    {hasSmsPackage && (
                                        <CheckBoxController
                                            form={form}
                                            name="smsEnabled"
                                            label={t('alarms.channel_sms')}
                                            disabled={isDetails}
                                        />
                                    )}
                                </div>
                            </FormItem>

                            <div className={styles.recipientsInfo}>
                                <span className={styles.recipientsLabel}>
                                    {t('alarms.recipients_label')}
                                    <FaInfoCircle className={styles.infoIcon} data-tip={t('alarms.recipients_tooltip')} data-for="recipientsTooltip" onClick={() => {
                                        const commType = selectedAlarmType?.name ? alarmTypeToCommunicationName[selectedAlarmType.name] : '';
                                        history.push(`/settings/communications${commType ? `?type=${commType}` : ''}`);
                                    }} />
                                    <ReactTooltip id="recipientsTooltip" />
                                </span>
                                {!recipientsLoading && recipients.length > 0 ? (
                                    <table className={styles.recipientsTable}>
                                        <thead>
                                            <tr>
                                                <th className={styles.recipientsTableHeader}>{t('users.title')}</th>
                                                <th className={styles.recipientsTableHeaderCentered}>{t('alarms.channel_alert')}</th>
                                                <th className={styles.recipientsTableHeaderCentered}>{t('alarms.channel_email')}</th>
                                                <th className={styles.recipientsTableHeaderCentered}>{t('alarms.channel_sms')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recipients.map(r => (
                                                <tr key={r.id}>
                                                    <td className={styles.recipientCell}>
                                                        <div className={styles.recipientName}>{r.name}</div>
                                                        <div className={styles.recipientContact}>
                                                            ({r.phoneNumber ? `${r.phoneNumber} - ` : ''}{r.email})
                                                        </div>
                                                    </td>
                                                    <td className={`${styles.recipientCellCentered} ${!alertEnabled ? styles.disabledCell : ''}`}
                                                        data-tip={!alertEnabled ? t('alarms.channel_disabled_tooltip') : undefined}
                                                        data-for={!alertEnabled ? `disabledTooltip-${r.id}-alert` : undefined}>
                                                        {alertEnabled ? (
                                                            r.hasAlert ? <FaCheck className={styles.checkIcon} /> : <FaTimes className={styles.crossIcon} />
                                                        ) : <span className={styles.disabledText}>-</span>}
                                                        {!alertEnabled && <ReactTooltip id={`disabledTooltip-${r.id}-alert`} />}
                                                    </td>
                                                    <td className={`${styles.recipientCellCentered} ${!emailEnabled ? styles.disabledCell : ''}`}
                                                        data-tip={!emailEnabled ? t('alarms.channel_disabled_tooltip') : undefined}
                                                        data-for={!emailEnabled ? `disabledTooltip-${r.id}-email` : undefined}>
                                                        {emailEnabled ? (
                                                            r.hasEmail ? <FaCheck className={styles.checkIcon} /> : <FaTimes className={styles.crossIcon} />
                                                        ) : <span className={styles.disabledText}>-</span>}
                                                        {!emailEnabled && <ReactTooltip id={`disabledTooltip-${r.id}-email`} />}
                                                    </td>
                                                    <td className={`${styles.recipientCellCentered} ${!smsEnabled ? styles.disabledCell : ''}`}
                                                        data-tip={!smsEnabled ? t('alarms.channel_disabled_tooltip') : undefined}
                                                        data-for={!smsEnabled ? `disabledTooltip-${r.id}-sms` : undefined}>
                                                        {smsEnabled ? (
                                                            r.hasSms ? <FaCheck className={styles.checkIcon} /> : <FaTimes className={styles.crossIcon} />
                                                        ) : <span className={styles.disabledText}>-</span>}
                                                        {!smsEnabled && <ReactTooltip id={`disabledTooltip-${r.id}-sms`} />}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : !recipientsLoading && (
                                    <span className={styles.noRecipients}>{t('alarms.no_recipients')}</span>
                                )}
                            </div>
                        </Col>
                    )}
                </Row>
            </Box>
        </form >
    );
});

export default AlarmTab;